import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid, GridFooterContainer, GridPagination, GridActionsCellItem } from '@mui/x-data-grid';
import debounce from 'lodash.debounce';
import axios from 'axios';
import ConfirmationDialog from './ConfirmationDialog';

const formatDateTime = (value, isDurationOnly) => {
  if (Number(isDurationOnly) === 1) {
    return '—';
  }
  if (!value) return '';
  return new Date(value).toLocaleString();
};

const normalizeLog = (log) => {
  if (!log) return log;
  const normalized = { ...log };
  const isDurationOnly = Number(log.isDurationOnly) === 1;
  normalized.isDurationOnly = isDurationOnly ? 1 : 0;
  normalized.duration = normalized.duration != null ? Number(normalized.duration) : null;
  if (isDurationOnly) {
    normalized.startTime = null;
    normalized.endTime = null;
  }
  return normalized;
};

const ProjectLogsTableDialog = ({
  open,
  onClose,
  logs,
  budget,
  projectName,
  projectDescription,
  projectId,
  publicId,
  token,
  serverUrl,
  onLogsUpdated,
  onMessage,
  onError,
  refreshProjects,
}) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 8 });
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [rows, setRows] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  const isReadOnly = !token;

  useEffect(() => {
    const processedLogs = logs.map((log, index) => {
      const normalized = normalizeLog(log);
      return {
        ...normalized,
        id: normalized?.id ?? log.id ?? index,
      };
    });
    setRows(processedLogs);
  }, [logs]);

  const fetchLatestLogs = useCallback(async () => {
    if (!token || !projectId) {
      return rows;
    }
    try {
      const res = await axios.get(`${serverUrl}/projects/${projectId}/logs`, {
        headers,
      });
      const rawLogs = Array.isArray(res.data) ? res.data : [];
      onLogsUpdated?.(rawLogs);
      const refreshedRows = rawLogs.map((log, index) => {
        const normalized = normalizeLog(log);
        return {
          ...normalized,
          id: normalized?.id ?? log.id ?? index,
        };
      });
      setRows(refreshedRows);
      return refreshedRows;
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to refresh logs';
      onError?.(`Refresh failed: ${message}`);
      throw error;
    }
  }, [token, projectId, serverUrl, headers, onLogsUpdated, onError, rows]);

  const handlePaginationModelChange = useCallback(
    debounce((model) => setPaginationModel(model), 300),
    []
  );

  const columns = [
    {
      field: 'startTime',
      headerName: 'Start Time',
      flex: 2,
      valueGetter: (params) => formatDateTime(params.row.startTime, params.row.isDurationOnly),
    },
    {
      field: 'endTime',
      headerName: 'End Time',
      flex: 2,
      valueGetter: (params) => formatDateTime(params.row.endTime, params.row.isDurationOnly),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      flex: 1,
      valueGetter: (params) => {
        const totalSeconds = Math.round(params.row.duration * 3600);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
      },
    },
    { field: 'activity', headerName: 'Activity', flex: 3 },
  ];

  if (!isReadOnly) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          key="delete"
          icon={<DeleteOutlineIcon />}
          label="Delete"
          onClick={() => setDeleteTarget(params.row)}
          color="inherit"
        />,
      ],
    });
  }

  const calculateTotalDuration = (data) => {
    const totalSeconds = data.reduce((sum, log) => sum + Math.round(((Number(log.duration) || 0) * 3600)), 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const calculateTotalHoursNumber = (data) => data.reduce((sum, log) => sum + (Number(log.duration) || 0), 0);

  const titleSuffixParts = [];
  if (projectName) {
    titleSuffixParts.push(projectName);
  }
  if (projectDescription) {
    titleSuffixParts.push(projectDescription);
  }
  const titleSuffix = titleSuffixParts.length ? `: ${titleSuffixParts.join(' — ')}` : '';

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${serverUrl}/projects/${projectId}/logs/${deleteTarget.id}`, {
        headers,
      });
      await fetchLatestLogs();
      refreshProjects?.();
      onMessage?.('Log deleted successfully');
      setDeleteTarget(null);
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to delete log';
      onError?.(`Delete failed: ${message}`);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Project Logs{titleSuffix}</DialogTitle>
      <DialogContent>
        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
          density="compact"
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSize={paginationModel.pageSize} // Explicitly set the page size
          pageSizeOptions={[5, 10, 20]} // Explicitly set page size options
          isCellEditable={() => false}
          disableRowSelectionOnClick
            components={{
              Footer: () => {
                const totalHrsNum = calculateTotalHoursNumber(rows);
                const bal = budget != null ? (Number(budget) - totalHrsNum) : null;
                return (
                  <GridFooterContainer>
                    <Box sx={{ padding: 1, textAlign: 'right', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1, textAlign: 'left', paddingLeft: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong>Total Hours: {calculateTotalDuration(rows)}</strong>
                        {budget != null && (
                          <span>Budget: {Number(budget).toFixed(2)}</span>
                        )}
                        {budget != null && (
                          <span style={{ color: bal < 0 ? 'red' : 'inherit' }}>
                            Balance: {bal.toFixed(2)}
                          </span>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, textAlign: 'right' }}>
                        <GridPagination />
                      </Box>
                    </Box>
                  </GridFooterContainer>
                );
              },
            }}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', pl: 1, pr: 2 }}>
        <Tooltip title="Share the table">
          <IconButton
            size="small"
            color="primary"
            sx={{ ml: 1 }}
            onClick={() => {
              const origin = window.location.origin;
              const url = publicId != null
                ? `${origin}/projects/p/${publicId}`
                : projectId != null
                  ? `${origin}/projects/id/${projectId}`
                  : `${origin}/projects/${encodeURIComponent(projectName || '')}`;
              setShareUrl(url);
              setShareOpen(true);
            }}
          >
            <ShareIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>

      {/* Share URL popup */}
      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Project Logs</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Public URL"
              value={shareUrl}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                if (navigator.clipboard && window.isSecureContext) {
                  await navigator.clipboard.writeText(shareUrl);
                }
              } catch {}
            }}
          >
            Copy
          </Button>
          <Button onClick={() => window.open(shareUrl, '_blank')}>
            Open
          </Button>
          <Button onClick={() => setShareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirmed}
        title="Delete Log"
        message="Are you sure you want to delete this log entry? This action cannot be undone."
      />
    </Dialog>
  );
};

export default ProjectLogsTableDialog;
